<template>
    <article @click="handleClick" :class="isChecked? 'checked':''">
        <div class="header">
            <div class="card-num">
                <div class="card-title-box">
                    <div class="card-title">{{ isLocked ? maskTitle : rawSimData.simCard?.simCardCode }}</div>
                    <div class="card-title-error" v-show="getDeviceInfo.boundStatus==='unbound'">({{ $t('dashboard.unbinded') }})</div>
                </div>
                <div class="button">
                    <ReoCheckbox
                        :modelValue="isChecked"
                        @change="changeSelect"
                        :isRound="simSelectMode"
                        :disabled="!canSelect"
                        v-if="select "
                    />
                    <span v-if="!select" class="icon-arrow-right-1 more-icon"></span>
                </div>
            </div>
            <div class="plan" :style="{backgroundImage: `url(${SimBgImg})`}">
                <div class="current_plan">
                    <p class="title">{{ $t('dashboard.current_plan') }}</p>
                    <p v-if="currentSubscription?.planInfo">
                        {{ $options.filters?.planName(currentSubscription?.planInfo) }}
                        <span v-if="currentSubscription.effectiveStatus==='inactive'" class="red">
                            ({{ $t('dashboard.status.expired') }})
                        </span>
                    </p>
                    <p v-else>{{ $t('dashboard.no_plan') }}</p>
                </div>
                <div v-if="simSelectMode" class="used">
                    <p class="title">{{ $t('dashboard.view.using_details.used') }}</p>
                    <ElProgress :percentage="usedPercentage" :show-text="false"/>
                </div>
            </div>
        </div>
        <div class="content" v-if="!simSelectMode && !isLocked">
            <div class="iccid">
                <p class="title">{{ $t('dashboard.device_detail.main_card.iccid') }}</p>
                <p>{{ $options.filters?.desensitizeString(rawSimData.simCard?.iccid) }}</p>
            </div>
            <div class="device" v-if="showSimDeviceInfo">
                <p class="title">{{ $t('my_sim.device_last_used') }}</p>
                <p v-if="isMobile">{{ deviceString }}</p>
                <OneLineText v-else :text="deviceString"/>
            </div>
        </div>
    </article>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import SimBgImg from 'IMAGE/dashboard/simInfoCard.svg';
import ReoCheckbox from '@/components/reoCheckbox.vue';
import { Progress as ElProgress } from 'element-ui';
import { SimManager as SimManager } from '@/lib/simManager';
import { uidHandler } from '@/lib/uidHandler';
import { mapGetters } from 'vuex';
import OneLineText from './OneLineText.vue';
import { BoundStatus, ICloudService, IReolinkDevice, ISimCard, ISubDevice } from '@reolink-cloud/interface';
import { config } from 'CONFIG';
import { getDeviceTitle } from '@/utils/business';
import reolinkGa from '@/common/js/gtagPageview';

export default Vue.extend({
    name: 'SimInfoCard',
    model: {
        prop: 'isChecked',
        event: 'change'
    },
    components: {
        ReoCheckbox,
        ElProgress,
        OneLineText
    },
    props: {
        devices: Array as PropType<IReolinkDevice[]>,
        subDevices: Array as PropType<ISubDevice[]>,
        isChecked: Boolean,
        select: Boolean,
        simSelectMode: Boolean,
        simManager: Object as PropType<SimManager>,
        batchCondition: String as PropType<string | undefined>
    },
    data() {

        return {
            SimBgImg,
            currentUid: '',
            boundStatus: 'bound' as BoundStatus,
            deviceInfo: {} as  IReolinkDevice | undefined,
        };

    },
    computed: {
        ...mapGetters<'isMobile'>( ['isMobile'] ),
        rawSimData(): ISimCard {

            return this.simManager.data;

        },
        deviceString(): string {

            return this.simManager.deviceString;

        },
        canSelect(): boolean {

            const noConditionOrFitCondition = this.batchCondition === undefined || ( this.batchCondition === this.simManager.batchCondition );
            return this.simSelectMode || ( this.currentSubscription !== undefined && noConditionOrFitCondition );

        },
        currentSubscription(): ICloudService | undefined {

            return this.simManager.currentSubscription;

        },
        usedPercentage(): number {

            const trafficData = this.simManager.data.currentDataUsage;
            const total = parseFloat( trafficData?.totalBytes ?? '1' );
            const used = parseFloat( trafficData?.usedBytes ?? '0' );
            return ( used / total ) * 100;

        },
        showSimDeviceInfo(): boolean {

            return config.showSimDeviceInfo;

        },
        isLocked(): boolean {

            return !!this.simManager?.data.esimDevice;

        },
        maskTitle(): string {

            if ( this.getDeviceInfo.boundStatus === 'unbound'  ) {

                return '***';

            } else  {

                const device = this.getDeviceInfo.deviceInfo;
                return getDeviceTitle( device );

            }

        },
        getDeviceInfo() {

            const computeInfo = () => {

                this.currentUid = this.simManager?.data.esimDevice;

                if ( this.isLocked ) {

                    this.boundStatus = this.devices.find( device => device.uid === this.currentUid )?.status ?? 'bound';
                    this.deviceInfo = this.devices.find( device => device.uid === this.currentUid );

                }

            };

            computeInfo();
            return {
                currentUid: this.currentUid,
                boundStatus: this.boundStatus,
                deviceInfo: this.deviceInfo
            };

        }

    },
    methods: {
        changeSelect() {

            this.$emit( 'change', !this.isChecked );

        },
        async handleClick() {

            if ( this.select ) {

                if ( this.canSelect ) {

                    this.changeSelect();

                }

            } else {

                const suffer = this.$route.path.includes( 'my-devices' ) ? 'Devices' : 'Dashboard';
                reolinkGa.sendSimpleEvent( `${suffer}-Sim-No Plan Active` );

                await this.$router.push({
                    path: '/user/my-sim',
                    query: {
                        ymh: uidHandler.encrypt( this.rawSimData.simCard?.iccid ?? '' ),
                        from: this.$route.path.includes( 'my-devices' ) ? 'my-devices' : 'cloud-dashboard'
                    }
                });

            }

        }
    },
    watch: {
        select( newVal: boolean ) {

            if ( !newVal ) {

                this.$emit( 'change', false );

            }

        },
    }
});
</script>

<style lang="less" scoped>
article {
    .reo-card();
    width: 322px;
    padding: 20px;
    box-sizing: border-box;
    border: 2px solid transparent;
    transition: border 0.25s;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 32px;
    @media @mobile {
        width: 100%;
    }

    &.checked {
        border: 2px solid @b_6;
    }

    &:hover {
        @media @pc {
            border: 2px solid @b_6;
        }
    }

    .header {
        font-size: 20px;
        font-weight: 500;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 16px;

        .card-num {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .card-title-box {
                display: flex;
                align-items: center;
                gap: 10px;

                .card-title{

                    @media @pc {
                        max-width:160px;
                        display: inline-block;
                    }
                }

                .card-title-error {
                    color: red;
                    font-size: 16px;
                }
            }

            .button {
                font-weight: 700;
            }
        }

        .plan {
            background-color: @bg_01;
            border-radius: 8px;
            background-repeat: no-repeat;
            background-position: right 7px bottom -20px;
            padding: 12px;
            display: flex;
            flex-direction: column;;
            gap: 8px;

            & p {
                font-size: 16px;
                font-weight: 500;
            }

            .current_plan {
                flex: 1;

                .title {
                    font-size: 14px;
                    margin-bottom: 8px;
                }
            }

            .used {
                margin-top: 16px;
                width: 182px;
                @media @mobile {
                    width: calc(100% - 78px);
                }

                .title {
                    margin-bottom: 8px;
                }

                /deep/ .el-progress-bar__outer {
                    background-color: @bg_02_h !important;

                    .el-progress-bar__inner {
                        background-color: @b_6;
                    }
                }
            }
        }
    }

    .content {
        display: flex;
        flex-direction: column;
        gap: 16px;

        & > div {
            display: flex;
            flex-direction: column;
            gap: 8px;
            font-size: 14px;
            font-weight: 500;

            & > p {
                font-weight: 500
            }
        }
    }

    .title {
        color: @font_secondary;
    }
}
</style>
